import { createContext, useContext } from "react";

import { OrganisationTypeConstants } from "../../../../constants";
import {
  CreatedProjects,
  LandholdingDetails,
  NewProjectJourneyState,
  NewProjectJourneyStep,
  ProjectAccessStepState,
  ProjectDetailsStepState,
  ProjectRow,
} from "../../../../models";
import { ProjectReferenceOptions } from "../../../../utils";

export type NewProjectJourneyVariant = "seed" | "default";
export type NewProjectJourneyCorners = "rounded" | "default";

/** One step of the journey: what the stepper labels it, and what renders while it is current. */
export type NewProjectJourneyStepDefinition = {
  key: NewProjectJourneyStep;
  Component: () => JSX.Element;
};

/**
 * The organisation creating the projects, gathered once here so neither step reads auth itself.
 * A developer creator already holds Full access on what it creates (REQ-003); any other creator is
 * recorded on the project by the api without this journey staging anything.
 */
export type CreatingOrganisation = {
  uuid: string;
  name: string;
  isDeveloper: boolean;
  /** Its type in the GraphQL enum form ("ASSET_MANAGER"), as searchOrganisations reports others. */
  organisationType: string;
  /** The full-access role its type carries ("Asset Manager"), the api's spelling of a targetRole. */
  role: string;
};

/** Everything a host needs once the journey has written: which projects exist, and under what. */
export type NewProjectJourneyCompletion = CreatedProjects & {
  projectCount: number;
  invitationCount: number;
  isLandholding: boolean;
  /** The rows as entered, whose keys index projectUuidByKey - for a host that needs the names. */
  rows: ProjectRow[];
  /** The landholding the rows were grouped under, when they were. */
  landholding: LandholdingDetails | null;
};

export interface NewProjectJourneyContextType {
  steps: NewProjectJourneyStepDefinition[];
  currentStep: NewProjectJourneyStep;
  inProgressStep: NewProjectJourneyStep;
  goToStep: (step: NewProjectJourneyStep) => void;
  completeStep: (step: NewProjectJourneyStep) => void;
  state: NewProjectJourneyState;
  updateDetails: (updater: (prev: ProjectDetailsStepState) => ProjectDetailsStepState) => void;
  updateAccess: (updater: (prev: ProjectAccessStepState) => ProjectAccessStepState) => void;
  setCreatedProjects: (created: CreatedProjects | null) => void;
  markSaveAttempted: () => void;
  options: ProjectReferenceOptions;
  currentUserType: OrganisationTypeConstants;
  creatingOrganisation: CreatingOrganisation;
  variant: NewProjectJourneyVariant;
  corners: NewProjectJourneyCorners;
  clearJourney: () => void;
  onComplete: (completion: NewProjectJourneyCompletion) => void;
}

export const NewProjectJourneyContext = createContext<NewProjectJourneyContextType | null>(null);

// Reading the context through this rather than useContext directly turns "cannot destructure
// undefined" on a step rendered outside its provider into a message that names the fix.
export const useNewProjectJourneyContext = (): NewProjectJourneyContextType => {
  const context = useContext(NewProjectJourneyContext);

  if (!context) {
    throw new Error("New project journey components must be rendered inside a NewProjectJourneyProvider.");
  }

  return context;
};
